'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ContactMethod {
  title: string;
  description: string;
  icon: string;
  action: string;
  availability: string;
}

interface ContactSupportProps {
  className?: string;
}

export default function ContactSupport({ className = '' }: ContactSupportProps) {
  const [showChatbot, setShowChatbot] = useState(false);
  const [ticketSubmitted, setTicketSubmitted] = useState(false);

  const contactMethods: ContactMethod[] = [
    {
      title: 'Live Chat',
      description: 'Get instant help from our support team',
      icon: 'ChatBubbleLeftRightIcon',
      action: 'Start Chat',
      availability: 'Available 24/7',
    },
    {
      title: 'Email Support',
      description: 'Send us a detailed message',
      icon: 'EnvelopeIcon',
      action: 'Send Email',
      availability: 'Response within 24 hours',
    },
    {
      title: 'Phone Support',
      description: 'Speak directly with our team',
      icon: 'PhoneIcon',
      action: 'Call Now',
      availability: 'Mon-Sat, 9 AM - 7 PM IST',
    },
    {
      title: 'Submit Ticket',
      description: 'Create a support ticket for tracking',
      icon: 'TicketIcon',
      action: 'Create Ticket',
      availability: 'Track your issue progress',
    },
  ];

  const handleContactAction = (method: string) => {
    if (method === 'Start Chat') {
      setShowChatbot(true);
    } else if (method === 'Create Ticket') {
      setTicketSubmitted(true);
      setTimeout(() => setTicketSubmitted(false), 3000);
    }
  };

  return (
    <section id="contact-support" className={`py-16 bg-white ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Contact Support
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our dedicated support team is here to help you with any questions or issues.
              Choose your preferred contact method below.
            </p>
          </div>

          {/* Contact Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {contactMethods.map((method, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg border border-gray-200 hover:border-[#42AF47]/40 hover:shadow-md transition"
              >
                <div className="w-12 h-12 bg-[#42AF47]/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon
                    name={method.icon as any}
                    size={24}
                    className="text-[#42AF47]"
                  />
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {method.title}
                </h3>

                <p className="text-sm text-gray-600 mb-3">
                  {method.description}
                </p>

                <p className="text-xs text-[#42AF47] mb-4">
                  {method.availability}
                </p>

                <button
                  onClick={() => handleContactAction(method.action)}
                  className="w-full px-4 py-2 bg-[#42AF47] text-white rounded-lg hover:brightness-110 hover:-translate-y-0.5 transition"
                >
                  {method.action}
                </button>
              </div>
            ))}
          </div>

          {/* Instant Assistant CTA */}
          <div className="bg-[#42AF47]/10 border border-[#42AF47]/20 rounded-lg p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-[#42AF47] rounded-full flex items-center justify-center">
                  <Icon
                    name="SparklesIcon"
                    size={32}
                    className="text-white"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">
                    Need Immediate Assistance?
                  </h3>
                  <p className="text-gray-600">
                    Our instant support assistant helps with common questions
                  </p>
                </div>
              </div>

              <button
                onClick={() => setShowChatbot(true)}
                className="px-6 py-3 bg-[#42AF47] text-white rounded-lg hover:brightness-110 hover:-translate-y-0.5 transition flex items-center gap-2"
              >
                <Icon name="ChatBubbleBottomCenterTextIcon" size={20} />
                <span>Chat with Assistant</span>
              </button>
            </div>
          </div>

          {/* Ticket Toast */}
          {ticketSubmitted && (
            <div className="fixed bottom-4 right-4 bg-[#42AF47] text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3">
              <Icon name="CheckCircleIcon" size={24} />
              <div>
                <p className="font-semibold">Ticket Submitted Successfully!</p>
                <p className="text-sm opacity-90">
                  Ticket #PK-{Math.floor(Math.random() * 10000)}
                </p>
              </div>
            </div>
          )}

          {/* Chatbot */}
          {showChatbot && (
            <div className="fixed bottom-4 right-4 w-96 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-lg shadow-2xl z-50">
              <div className="flex items-center justify-between p-4 bg-[#42AF47] text-white rounded-t-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Icon name="ChatBubbleBottomCenterTextIcon" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold">ParkPing Assistant</h4>
                    <p className="text-xs opacity-90">Online now</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowChatbot(false)}
                  className="hover:bg-white/20 p-1 rounded transition"
                >
                  <Icon name="XMarkIcon" size={20} />
                </button>
              </div>

              <div className="h-96 p-4 overflow-y-auto bg-gray-50">
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 bg-[#42AF47] rounded-full flex items-center justify-center">
                      <Icon name="SparklesIcon" size={16} className="text-white" />
                    </div>
                    <div className="bg-white p-3 rounded-lg max-w-[80%]">
                      <p className="text-sm text-gray-700">
                        Hello! I am your ParkPing support assistant. How can I help you today?
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <div className="bg-[#42AF47] text-white p-3 rounded-lg max-w-[80%]">
                      <p className="text-sm">I need help activating my card</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-gray-200">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Type your message..."
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#42AF47]"
                  />
                  <button className="w-10 h-10 bg-[#42AF47] text-white rounded-lg flex items-center justify-center hover:brightness-110 transition">
                    <Icon name="PaperAirplaneIcon" size={20} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
